import { useServices } from '@/app/providers';
import { useI18n } from '@/shared/i18n';
import type { TenantSelection } from '@yawi/types';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { TenantSwitchModal } from './TenantSwitchModal';

const getInitials = (name: string) =>
  name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

interface TenantBadgeProps {
  collapsed?: boolean;
}

export function TenantBadge({ collapsed }: TenantBadgeProps) {
  const { currentTenant, systemSettings, userRepo } = useServices();
  const { t } = useI18n();

  // Tenants del mismo correo, no membresías de esta cuenta: el alta de un
  // residencial crea un usuario nuevo por tenant (ver /auth/my-accounts).
  const [accounts, setAccounts] = useState<TenantSelection[]>([]);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<TenantSelection | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    userRepo
      .getMyAccounts()
      .then((list) => {
        if (!cancelled) setAccounts(list);
      })
      .catch(() => {
        // Sin lista no hay selector; el badge sigue mostrando el tenant actual.
      });
    return () => {
      cancelled = true;
    };
  }, [userRepo]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const name = currentTenant?.name ?? systemSettings?.condoName;
  if (!name) return null;

  const initials = (
    <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0 text-white font-bold text-sm shadow-sm">
      {getInitials(name)}
    </div>
  );

  if (collapsed) {
    return (
      <div className="flex justify-center py-2" title={name}>
        {initials}
      </div>
    );
  }

  const identity = (
    <>
      {initials}
      <div className="overflow-hidden flex-1 min-w-0 text-left">
        <p className="text-sm font-semibold text-theme truncate leading-tight">{name}</p>
        <p className="text-[10px] text-theme-tertiary leading-tight">{t('tenantSwitch.subtitle')}</p>
      </div>
    </>
  );

  // Con una sola cuenta no hay nada que elegir: se mantiene el bloque estático.
  if (accounts.length <= 1) {
    return <div className="flex items-center gap-3 px-2 py-2 rounded-xl">{identity}</div>;
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-surface-secondary transition-colors"
      >
        {identity}
        <ChevronDown
          size={16}
          className={`text-theme-tertiary flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 glass-heavy rounded-xl shadow-xl border border-theme-light py-1 max-h-72 overflow-y-auto">
          <p className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-theme-tertiary">
            {t('tenantSwitch.title')}
          </p>
          {accounts.map((account) => {
            const isCurrent = account.tenantId === currentTenant?.id;
            return (
              <button
                key={`${account.userId}-${account.tenantId}`}
                type="button"
                disabled={isCurrent}
                onClick={() => {
                  setOpen(false);
                  setTarget(account);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                  isCurrent ? 'text-theme font-semibold' : 'text-theme-secondary hover:bg-surface-secondary'
                }`}
              >
                <span className="flex-1 truncate">{account.tenantName}</span>
                {isCurrent && <Check size={14} className="text-primary-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}

      {target && <TenantSwitchModal tenant={target} onClose={() => setTarget(null)} />}
    </div>
  );
}
